import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { PartnersIndex } from "@itaas/pep-contracts";
import { PARTNER_TIER_LABELS, PARTNER_TYPE_LABELS } from "@itaas/pep-contracts";
import { getPartners } from "../api/pep-api";
import { SiteLayout } from "../components/layout/SiteLayout";
import { SeoHead } from "../components/seo/SeoHead";

const PARTNER_TYPES_STATIC = [
  {
    title: "Intégrateurs & ESN",
    desc: "Déployez ITaaS chez vos clients PME et enterprise. Formation, personnalisation et support de premier niveau.",
    benefits: ["Marges revendeur", "Formation certifiante (à venir)", "Support technique dédié"],
  },
  {
    title: "Revendeurs logiciels",
    desc: "Élargissez votre portefeuille avec une suite modulaire africaine — ERP, RH, mines, santé.",
    benefits: ["Catalogue modulaire", "Essais clients facilités", "Co-marketing"],
  },
  {
    title: "Bailleurs & ONG",
    desc: "Équipez vos projets de gestion transparente — audit, multi-sites, traçabilité.",
    benefits: ["Reporting bailleurs", "Multi-projets", "Tarifs ONG"],
  },
  {
    title: "Hébergeurs & cloud local",
    desc: "Hébergez ITaaS en souveraineté données pour clients exigeant le datacenter local.",
    benefits: ["Déploiement privé", "SLA local", "Support conjoint"],
  },
];

export function PartnersPage() {
  const [data, setData] = useState<Pick<PartnersIndex, "programTiers" | "partners"> | null>(null);

  useEffect(() => {
    getPartners().then(setData).catch(console.error);
  }, []);

  const featured = data?.partners.filter((p) => p.featured) ?? [];
  const tiers = data?.programTiers ?? [];

  return (
    <SiteLayout>
      <SeoHead
        title="Partenaires & revendeurs"
        description="Devenez partenaire ITaaS — intégrateurs, revendeurs, ONG et hébergeurs en RDC et Afrique."
        keywords={["partenaire ITaaS", "revendeur logiciel RDC", "intégrateur ERP Congo"]}
        canonicalPath="/partners"
      />
      <div className="pep-page-hero py-5 mb-0">
        <div className="container">
          <h1 className="display-5 fw-bold mb-3">Partenaires</h1>
          <p className="lead col-lg-8 mb-0 opacity-90">
            Rejoignez l&apos;écosystème ITaaS et accompagnez la transformation digitale en Afrique.
          </p>
        </div>
      </div>
      <div className="container py-5">
        <div className="row g-4 mb-5">
          {PARTNER_TYPES_STATIC.map((p) => (
            <div className="col-md-6" key={p.title}>
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <h2 className="h5 fw-bold">{p.title}</h2>
                  <p className="text-secondary">{p.desc}</p>
                  <ul className="small mb-0">
                    {p.benefits.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {tiers.length > 0 && (
          <>
            <h2 className="h4 fw-bold mb-4">Programme partenaire</h2>
            <div className="row g-4 mb-5">
              {tiers.map((tier) => (
                <div className="col-md-6 col-lg-3" key={tier.id}>
                  <div className={`card border-0 shadow-sm h-100 pep-tier-card pep-tier-${tier.id}`}>
                    <div className="card-body p-4">
                      <h3 className="h5 fw-bold">{tier.displayName}</h3>
                      <p className="small text-secondary">{tier.descriptionFr}</p>
                      {tier.minAnnualRevenueUsd && (
                        <p className="small fw-semibold text-primary">
                          À partir de {tier.minAnnualRevenueUsd.toLocaleString("fr-FR")} USD/an
                        </p>
                      )}
                      <ul className="small mb-0">
                        {tier.benefits.map((b) => (
                          <li key={b}>{b}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {featured.length > 0 && (
          <>
            <h2 className="h4 fw-bold mb-4">Partenaires à la une</h2>
            <div className="row g-4 mb-5">
              {featured.map((p) => (
                <div className="col-md-6 col-lg-4" key={p.slug} id={p.slug}>
                  <article className="card border-0 shadow-sm h-100">
                    <div className="card-body p-4">
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        <span className="badge text-bg-light border">
                          {PARTNER_TYPE_LABELS[p.type]}
                        </span>
                        {p.tier && (
                          <span className="badge text-bg-primary">
                            {PARTNER_TIER_LABELS[p.tier]}
                          </span>
                        )}
                      </div>
                      <h3 className="h5 fw-bold">{p.displayNameFr ?? p.displayName}</h3>
                      <p className="text-secondary small">{p.descriptionFr}</p>
                      <p className="small text-secondary mb-0">
                        {p.regions.join(" · ")}
                      </p>
                    </div>
                  </article>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="p-5 rounded-4 pep-cta-band text-white text-center">
          <h2 className="h4 fw-bold mb-3">Devenir partenaire</h2>
          <p className="opacity-90 mb-4">
            Programme structuré Bronze → Platinum — inscrivez-vous dès maintenant.
          </p>
          <Link className="btn btn-light btn-lg" to="/contact">
            Candidature partenaire
          </Link>
        </div>
      </div>
    </SiteLayout>
  );
}
